import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Alert,
  CircularProgress,
  Avatar,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Phone as PhoneIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Visibility as ViewerIcon,
  LocalHospital as CaregiverIcon,
  Warning as EmergencyIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Check as CheckIcon,
  HourglassEmpty as PendingIcon
} from '@mui/icons-material';

interface FamilyMember {
  id: string;
  patientId: string;
  name: string;
  email?: string;
  phone: string;
  role: 'viewer' | 'caregiver' | 'emergency_contact';
  status: 'active' | 'pending';
  invitedAt: string;
  acceptedAt?: string;
}

interface FamilyCareCircleProps {
  patientId: string;
}

const FamilyCareCircle: React.FC<FamilyCareCircleProps> = ({ patientId }) => {
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [openInvite, setOpenInvite] = useState(false);
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null);
  const [inviteForm, setInviteForm] = useState({
    name: '',
    phone: '+569',
    email: '',
    role: 'viewer' as 'viewer' | 'caregiver' | 'emergency_contact'
  });

  useEffect(() => {
    fetchFamilyMembers();
  }, [patientId]);

  const fetchFamilyMembers = async () => {
    try {
      const response = await fetch(`http://localhost:3000/api/v1/patients/${patientId}/family`);
      const data = await response.json();
      if (data.success) {
        setFamilyMembers(data.data.familyMembers);
      }
    } catch (error) {
      console.error('Error fetching family members:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'caregiver': return <CaregiverIcon />;
      case 'emergency_contact': return <EmergencyIcon />;
      default: return <ViewerIcon />;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'caregiver': return 'Cuidador';
      case 'emergency_contact': return 'Contacto de Emergencia';
      default: return 'Observador';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'caregiver': return 'primary';
      case 'emergency_contact': return 'error';
      default: return 'default';
    }
  };

  const handleInvite = async () => {
    try {
      const response = await fetch(`http://localhost:3000/api/v1/patients/${patientId}/family/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inviteForm)
      });
      
      const data = await response.json();
      if (data.success) {
        fetchFamilyMembers();
        setOpenInvite(false);
        setInviteForm({ name: '', phone: '+569', email: '', role: 'viewer' });
        // In real app, show success notification
      }
    } catch (error) {
      console.error('Error inviting family member:', error);
    }
  };

  const handleUpdateRole = async (memberId: string, newRole: string) => {
    try {
      const response = await fetch(`http://localhost:3000/api/v1/family/${memberId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole })
      });
      
      if (response.ok) {
        fetchFamilyMembers();
        setEditingMember(null);
      }
    } catch (error) {
      console.error('Error updating role:', error);
    }
  };

  const handleRemove = async (memberId: string) => {
    if (window.confirm('¿Está seguro de eliminar este miembro del círculo de cuidado?')) {
      try {
        const response = await fetch(`http://localhost:3000/api/v1/family/${memberId}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          fetchFamilyMembers();
        }
      } catch (error) {
        console.error('Error removing family member:', error);
      }
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Mi Círculo de Cuidado
        </Typography>
        <Button
          size="small"
          startIcon={<PersonAddIcon />}
          onClick={() => setOpenInvite(true)}
          variant="outlined"
        >
          Invitar
        </Button>
      </Box>

      {familyMembers.length === 0 ? (
        <Alert severity="info">
          No hay miembros en tu círculo de cuidado. Invita a familiares para que puedan ayudarte en caso de emergencia.
        </Alert>
      ) : (
        <List dense>
          {familyMembers.map((member) => (
            <React.Fragment key={member.id}>
              <ListItem>
                <ListItemIcon>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: getRoleColor(member.role) + '.main' }}>
                    {member.name.charAt(0)}
                  </Avatar>
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {member.name}
                      {member.status === 'pending' && (
                        <Chip 
                          icon={<PendingIcon />} 
                          label="Pendiente" 
                          size="small" 
                          color="warning"
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" display="block">
                        {getRoleIcon(member.role)} {getRoleLabel(member.role)}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <WhatsAppIcon sx={{ fontSize: 14 }} />
                        <Typography variant="caption">{member.phone}</Typography>
                      </Box>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Editar rol">
                    <IconButton 
                      edge="end" 
                      size="small"
                      onClick={() => setEditingMember(member)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar">
                    <IconButton 
                      edge="end" 
                      size="small"
                      onClick={() => handleRemove(member.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          ))}
        </List>
      )}

      <Box sx={{ mt: 2, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Permisos:</strong><br />
          • <ViewerIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> Observador: Ve tu estado de salud<br />
          • <CaregiverIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> Cuidador: Puede reportar síntomas por ti<br />
          • <EmergencyIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> Emergencia: Recibe alertas críticas
        </Typography>
      </Box>

      {/* Invite Dialog */}
      <Dialog open={openInvite} onClose={() => setOpenInvite(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invitar al Círculo de Cuidado</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Nombre completo"
              value={inviteForm.name}
              onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
              fullWidth
              required
            />
            
            <TextField
              label="Teléfono (WhatsApp)"
              value={inviteForm.phone}
              onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
              fullWidth
              required
              helperText="Se enviará invitación por WhatsApp"
              InputProps={{
                startAdornment: <WhatsAppIcon sx={{ mr: 1, color: 'success.main' }} />
              }}
            />
            
            <TextField
              label="Email (opcional)"
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              fullWidth
              type="email"
            />
            
            <FormControl fullWidth>
              <InputLabel>Tipo de acceso</InputLabel>
              <Select
                value={inviteForm.role}
                onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value as any })}
                label="Tipo de acceso"
              >
                <MenuItem value="viewer">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ViewerIcon /> Observador
                  </Box>
                </MenuItem>
                <MenuItem value="caregiver">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CaregiverIcon /> Cuidador
                  </Box>
                </MenuItem>
                <MenuItem value="emergency_contact">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmergencyIcon /> Contacto de Emergencia
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenInvite(false)}>Cancelar</Button>
          <Button 
            onClick={handleInvite} 
            variant="contained" 
            disabled={!inviteForm.name || !inviteForm.phone}
          >
            Enviar Invitación
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={!!editingMember} onClose={() => setEditingMember(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cambiar Rol de {editingMember?.name}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Nuevo rol</InputLabel>
            <Select
              value={editingMember?.role || ''}
              onChange={(e) => editingMember && handleUpdateRole(editingMember.id, e.target.value)}
              label="Nuevo rol"
            >
              <MenuItem value="viewer">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ViewerIcon /> Observador
                </Box>
              </MenuItem>
              <MenuItem value="caregiver">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CaregiverIcon /> Cuidador
                </Box>
              </MenuItem>
              <MenuItem value="emergency_contact">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmergencyIcon /> Contacto de Emergencia
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingMember(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default FamilyCareCircle;